import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Mail, Phone, Sparkles, Send } from 'lucide-react';
import { PublicNav } from '../components/PublicNav';
import { Logo } from '../components/Brand';
import { MessageGregBubble } from '../components/MessageGregBubble';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { api } from '../api';
import { trackDemoLead } from '../lib/analytics';

// Contact page — a dedicated route (/contact) that showcases K9os contact
// details and hosts the same lead-capture form used inside the landing
// dialog. Submits to POST /api/demo-requests which triggers a Resend email
// alert to the K9os founder.
export default function Contact() {
  // Route-specific SEO meta — sets a Contact-specific title + canonical
  // that self-references /contact, so Google indexes this page as
  // "Contact K9os" rather than treating it as an alias of the root and
  // out-ranking the landing page under the "k9os.app" search query.
  useEffect(() => {
    document.title = 'Contact K9os — Book a Free Demo of All-in-One Dog Training Software';
    const setMeta = (selector, attr, content) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.startsWith('link') ? 'link' : 'meta');
        if (selector.includes('name=')) el.setAttribute('name', selector.match(/name="([^"]+)"/)[1]);
        if (selector.includes('property=')) el.setAttribute('property', selector.match(/property="([^"]+)"/)[1]);
        if (selector.includes('rel=')) el.setAttribute('rel', selector.match(/rel="([^"]+)"/)[1]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, content);
    };
    setMeta('meta[name="description"]', 'content', 'Book a free K9os demo. Talk with the K9os founder about a custom-built dog-training website, kennel bookings, POS, memberships, contracts, and marketing — all unified in one app for your dog-training business.');
    setMeta('meta[property="og:title"]', 'content', 'Contact K9os — Book a Free Demo');
    setMeta('meta[property="og:url"]', 'content', 'https://k9os.app/contact');
    setMeta('link[rel="canonical"]', 'href', 'https://k9os.app/contact');
  }, []);

  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', facility_name: '',
    location: '', website: '', notes: '',
  });
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error('Please share your name and email so we can reach you.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/demo-requests', form);
      trackDemoLead({ source: 'contact_page', region: form.location || 'unspecified' });
      toast.success("You're on the list — Greg will reach out shortly.");
      setForm({ full_name: '', email: '', phone: '', facility_name: '', location: '', website: '', notes: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit right now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='min-h-screen bg-white dark:bg-[#0B1220] text-slate-900 dark:text-slate-100'>
      <PublicNav />

      {/* Hero */}
      <section
        className='relative overflow-hidden border-b border-slate-200 dark:border-slate-800'
        style={{
          background:
            'radial-gradient(120% 60% at 50% 0%, rgba(125,227,217,0.18) 0%, transparent 55%),' +
            'radial-gradient(80% 50% at 90% 100%, rgba(200,16,46,0.10) 0%, transparent 60%)',
        }}
      >
        <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center relative'>
          <div className='inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#7de3d9] mb-3'>
            <Sparkles className='w-3.5 h-3.5' /> Get in touch
          </div>
          <h1 className='font-display text-4xl sm:text-5xl font-bold tracking-tight' data-testid='contact-heading'>
            Ready to see K9os in action?
          </h1>
          <p className='text-slate-600 dark:text-slate-300 mt-4 max-w-2xl mx-auto text-base sm:text-lg'>
            Tell us about your training facility and we&apos;ll walk you through the platform &mdash;
            bookings, POS, memberships, contracts, and marketing all under one login.
          </p>
        </div>
      </section>

      {/* Contact grid — lead form promoted to the TOP (mobile) / LEFT
          (desktop) so visitors land straight on the "Schedule a Free
          Demo" form. Contact-info cards (email + click-to-call phone)
          sit below on mobile or to the right on desktop as a
          supporting sidebar. */}
      <section className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16'>
        <div className='grid lg:grid-cols-5 gap-8'>
          {/* Left / top — Lead form (primary). Full-width on mobile, 3/5 on lg. */}
          <div className='lg:col-span-3 order-1'>
            <Card
              className='p-6 sm:p-8 border-slate-200 dark:border-slate-700 dark:bg-slate-900/60 shadow-[0_10px_40px_-15px_rgba(11,58,135,0.25)]'
              data-testid='contact-form-card'
            >
              <h2 className='font-display text-2xl font-bold mb-1'>Schedule a Free Demo</h2>
              <p className='text-sm text-slate-500 dark:text-slate-400 mb-5'>
                20-minute walkthrough &mdash; tailored to your facility.
              </p>
              <form onSubmit={onSubmit} className='space-y-4' data-testid='contact-lead-form'>
                <div>
                  <Label htmlFor='c-name'>Your full name *</Label>
                  <Input id='c-name' value={form.full_name} onChange={onChange('full_name')} placeholder='Jane Trainer' required data-testid='contact-input-name' />
                </div>
                <div className='grid sm:grid-cols-2 gap-3'>
                  <div>
                    <Label htmlFor='c-email'>Email *</Label>
                    <Input id='c-email' type='email' value={form.email} onChange={onChange('email')} placeholder='you@yourfacility.com' required data-testid='contact-input-email' />
                  </div>
                  <div>
                    <Label htmlFor='c-phone'>Phone (optional)</Label>
                    <Input id='c-phone' type='tel' value={form.phone} onChange={onChange('phone')} placeholder='(555) 555-0123' data-testid='contact-input-phone' />
                  </div>
                </div>
                <div>
                  <Label htmlFor='c-facility'>Facility / Business (optional)</Label>
                  <Input id='c-facility' value={form.facility_name} onChange={onChange('facility_name')} placeholder='Your K9 training business' data-testid='contact-input-facility' />
                </div>
                <div className='grid sm:grid-cols-2 gap-3'>
                  <div>
                    <Label htmlFor='c-location'>City &amp; State (optional)</Label>
                    <Input id='c-location' value={form.location} onChange={onChange('location')} placeholder='Austin, TX' data-testid='contact-input-location' />
                  </div>
                  <div>
                    <Label htmlFor='c-website'>Website or social link (optional)</Label>
                    <Input id='c-website' type='url' inputMode='url' value={form.website} onChange={onChange('website')} placeholder='https://yourfacility.com or @handle' data-testid='contact-input-website' />
                  </div>
                </div>
                <div>
                  <Label htmlFor='c-notes'>What would you like to see? (optional)</Label>
                  <Textarea id='c-notes' rows={4} value={form.notes} onChange={onChange('notes')} placeholder='Bookings, memberships, POS, marketing…' data-testid='contact-input-notes' />
                </div>
                <Button
                  type='submit'
                  disabled={submitting}
                  className='k9-demo-cta w-full bg-[#C8102E] hover:bg-[#A50D26] text-white h-14 text-base sm:text-lg font-bold rounded-xl'
                  data-testid='contact-submit-btn'
                >
                  {submitting ? (
                    <>
                      <Loader2 className='w-5 h-5 mr-2 animate-spin' /> Sending…
                    </>
                  ) : (
                    <>
                      <Send className='w-5 h-5 mr-2' /> Request a Demo
                    </>
                  )}
                </Button>
                <p className='text-[11px] text-slate-400 dark:text-slate-500 text-center'>
                  By submitting you agree we may email you about your demo request. No spam &mdash; ever.
                </p>
              </form>
            </Card>
          </div>

          {/* Right / bottom — Supporting contact info sidebar (2/5 on lg).
              Email + click-to-call phone (718-691-2711). Sits below the
              form on mobile, to the right on desktop. */}
          <div className='lg:col-span-2 order-2 space-y-4'>
            <Card
              className='p-5 border-slate-200 dark:border-slate-700 dark:bg-slate-900/60'
              data-testid='contact-info-email-card'
            >
              <div className='flex items-start gap-3'>
                <div className='w-10 h-10 rounded-lg bg-[#1B3A87]/10 dark:bg-[#93C5FD]/10 flex items-center justify-center text-[#1B3A87] dark:text-[#93C5FD] shrink-0'>
                  <Mail className='w-5 h-5' />
                </div>
                <div className='min-w-0'>
                  <div className='text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400'>Email</div>
                  <a
                    href='mailto:GregMiceli2018@gmail.com'
                    className='block font-semibold text-slate-900 dark:text-slate-100 hover:text-[#1B3A87] dark:hover:text-[#93C5FD] break-all'
                    data-testid='contact-email-link'
                  >
                    GregMiceli2018@gmail.com
                  </a>
                  <div className='text-xs text-slate-500 dark:text-slate-400 mt-1'>
                    We reply within one business day.
                  </div>
                </div>
              </div>
            </Card>

            <Card
              className='p-5 border-slate-200 dark:border-slate-700 dark:bg-slate-900/60'
              data-testid='contact-info-phone-card'
            >
              <div className='flex items-start gap-3'>
                <div className='w-10 h-10 rounded-lg bg-[#C8102E]/10 flex items-center justify-center text-[#C8102E] shrink-0'>
                  <Phone className='w-5 h-5' />
                </div>
                <div className='min-w-0'>
                  <div className='text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400'>Direct line</div>
                  <a
                    href='tel:+17186912711'
                    className='block font-semibold text-slate-900 dark:text-slate-100 hover:text-[#C8102E] text-lg tracking-tight'
                    data-testid='contact-phone-link'
                  >
                    (718) 691-2711
                  </a>
                  <div className='text-xs text-slate-500 dark:text-slate-400 mt-1'>
                    Tap to call &mdash; or submit the form for a scheduled walkthrough.
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      <footer className='py-10 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row gap-4 md:gap-6 items-center justify-between text-center md:text-left'>
          <Logo />
          <div className='text-xs text-slate-500 dark:text-slate-400'>© {new Date().getFullYear()} K9os.app · All rights reserved.</div>
        </div>
      </footer>

      {/* Floating quick-chat bubble — bottom-right, opens a compact form
          that posts to the same lead pipeline (tagged [Quick Chat]). */}
      <MessageGregBubble />
    </div>
  );
}
